import React, { useEffect, useMemo, useState } from 'react';
import Icon from '@/components/Icon';
import Topbar from '@/layout/Topbar';
import { useShell } from '@/layout/AppShell';
import { useCan } from '@/auth/AuthContext';
import { ApiError, api } from '@/lib/api';
import TimetableSlotEditModal from './TimetableSlotEditModal';

const ALL_CLASSES = [0, 1, 2, 3, 4];

function classesLabel(cn) {
  if (cn === null || cn === undefined) return 'Все классы';
  if (!cn.length) return 'Не назначено';
  // Слитный диапазон → «0–4», иначе перечислить
  const sorted = [...cn].sort((a, b) => a - b);
  const isContinuous = sorted.every((v, i) => i === 0 || v === sorted[i - 1] + 1);
  if (sorted.length > 2 && isContinuous) {
    return sorted[0] + '–' + sorted[sorted.length - 1] + ' классы';
  }
  return sorted.map((n) => n + ' кл.').join(', ');
}

export default function TimetableSlotsPage() {
  const { tweaks, updateTweak } = useShell();
  const can = useCan();
  const canWrite  = can('timetable_slots', 'write');
  const canDelete = can('timetable_slots', 'delete');

  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [editing, setEditing] = useState(null); // null | 'new' | object | { presetPosition: N }

  const reload = () => {
    setLoading(true); setError(null);
    api.get('/api/timetable-slots')
      .then((res) => setRows(res.rows || []))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoading(false));
  };
  useEffect(reload, []);

  // Сгруппировано по position. Каждый position может иметь несколько вариантов
  // (например, для классов 0-1 одни звонки, для 2-4 — другие).
  const groups = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      if (!map.has(r.position)) map.set(r.position, []);
      map.get(r.position).push(r);
    }
    // Сортируем варианты внутри группы по времени начала
    for (const arr of map.values()) arr.sort((a, b) => a.timeStart.localeCompare(b.timeStart));
    return Array.from(map.entries()).sort((a, b) => a[0] - b[0]);
  }, [rows]);

  const onDelete = async (slot) => {
    if (!canDelete) return;
    if (!confirm(`Удалить слот «Урок ${slot.position}, ${slot.timeStart}–${slot.timeEnd}»?`)) return;
    try {
      await api.delete('/api/timetable-slots/' + slot.id);
      setRows((prev) => prev.filter((r) => r.id !== slot.id));
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Не удалось удалить');
    }
  };

  // Подсказка следующего номера = max(position) + 1
  const nextPosition = rows.length ? Math.max(...rows.map((r) => r.position)) + 1 : 1;

  return (
    <>
      <Topbar
        breadcrumb={['Обучение', 'Время уроков']}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />
      <div className="ref-page">
        <div className="ref-toolbar">
          <span className="ref-toolbar__title">
            Время уроков (звонки) {!loading && <span className="muted">· {rows.length}</span>}
          </span>
          <span style={{ marginLeft: 14, fontSize: 12, color: 'var(--fg-muted)', maxWidth: 540 }}>
            Если для разных классов разные звонки на одном уроке — добавляйте отдельный вариант с указанием классов.
          </span>
          <div className="ref-toolbar__spacer" />
          <button
            className="btn btn--primary btn--sm"
            onClick={() => setEditing({ presetPosition: nextPosition })}
            disabled={!canWrite}
            title={canWrite ? undefined : 'Нет прав на запись'}
          >
            <Icon name="plus" size={13} /> Добавить слот
          </button>
        </div>

        <div className="ref-content">
          {error && <div style={{ color: 'var(--red-fg)', padding: 16 }}>Ошибка: {error}</div>}
          {loading ? (
            <div className="ref-empty">Загрузка…</div>
          ) : groups.length === 0 ? (
            <div className="ref-empty">Слоты ещё не созданы. Добавьте первый.</div>
          ) : (
            <div className="tt-grid">
              {groups.map(([position, variants]) => (
                <div key={position} className="tt-row">
                  <div className="tt-row__pos">
                    <span className="tt-row__num">{position}</span>
                    <span className="tt-row__lbl">урок</span>
                  </div>
                  <div className="tt-row__variants">
                    {variants.map((v) => (
                      <div key={v.id} className="tt-variant">
                        <div className="tt-variant__time mono">
                          {v.timeStart} — {v.timeEnd}
                        </div>
                        <div className="tt-variant__classes">
                          {v.classNumbers === null
                            ? <span className="tt-variant__chip tt-variant__chip--all">Все классы</span>
                            : v.classNumbers.length === 0
                              ? <span className="tt-variant__chip tt-variant__chip--warn">Не назначено</span>
                              : <span className="tt-variant__chip">{classesLabel(v.classNumbers)}</span>}
                        </div>
                        {(canWrite || canDelete) && (
                          <div className="ref-row-actions">
                            {canWrite && (
                              <button onClick={() => setEditing(v)} title="Изменить">
                                <Icon name="edit" size={14} />
                              </button>
                            )}
                            {canDelete && (
                              <button className="is-danger" onClick={() => onDelete(v)} title="Удалить">
                                <Icon name="x" size={14} />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                    {canWrite && (
                      <button
                        className="tt-variant tt-variant--add"
                        onClick={() => setEditing({ presetPosition: position })}
                        title="Добавить вариант для другого диапазона классов"
                      >
                        <Icon name="plus" size={12} />
                        <span>Вариант</span>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {editing && (
        <TimetableSlotEditModal
          mode={editing.id ? 'edit' : 'create'}
          slot={editing.id ? editing : null}
          presetPosition={editing.presetPosition || (editing.position ?? null)}
          onClose={() => setEditing(null)}
          onSaved={(s) => {
            setRows((prev) => {
              const i = prev.findIndex((r) => r.id === s.id);
              if (i >= 0) { const n = prev.slice(); n[i] = s; return n; }
              return [...prev, s];
            });
          }}
        />
      )}
    </>
  );
}
